import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, ClipboardList, Users } from "lucide-react";
import { inboxCount } from "../lib/sharing";

interface Props {
  hidden?: boolean;
}

// Tab order: primary tracking loop (Today ↔ History) stays adjacent;
// Partners is a meta/social feature so it sits to the right.
const navItems = [
  { to: "/", label: "Today", Icon: Home, end: true },
  { to: "/history", label: "History", Icon: ClipboardList, end: false },
  { to: "/partners", label: "Partners", Icon: Users, end: false }
] as const;

export default function Nav({ hidden = false }: Props) {
  // Show a small unread-count dot on the Partners tab when there are
  // pending shared-meal invites. Refresh on every route change so accepting
  // a share immediately clears the badge.
  const location = useLocation();
  const [badgeCount, setBadgeCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    inboxCount()
      .then((n) => {
        if (!cancelled) setBadgeCount(n);
      })
      .catch(() => {
        /* swallow — badge is best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (hidden) return null;
  return (
    <nav
      className="fixed bottom-0 inset-x-0 max-w-md md:max-w-2xl lg:max-w-3xl mx-auto bg-slate-950/85 backdrop-blur border-t border-slate-800 flex"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {navItems.map(({ to, label, Icon, end }) => {
        const showBadge = to === "/partners" && badgeCount > 0;
        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] relative ${
                isActive ? "text-emerald-400 font-semibold" : "text-slate-400"
              }`
            }
          >
            <div className="relative">
              <Icon className="w-5 h-5" strokeWidth={2} />
              {showBadge && (
                <span className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">
                  {badgeCount > 9 ? "9+" : badgeCount}
                </span>
              )}
            </div>
            <span>{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
